import React from 'react';
import { Component, Position, PuzzleTabStrip, VBox } from "puzzle";
import _ from 'lodash';
import {
  DOCUMENT,
  LANGUAGE_DIRECTIONS,
  RESPONSE_LANGUAGE_DIRECTIONS,
  SUBMISSION_DETAILS_PROPS,
  SUBMISSION_DETAILS_STATE
} from "../../Types";
import {
  addDeletedTargetLanguages,
  createTargetLanguageTagClass,
  getMatchingDisplayName
} from "../../../util/Util";
import WordCountLink from "../../../util/WordCountLink/WordCountLink";
import PoliciesManager from "../../../util/Policies";
import { PuzzleButton, Container } from "puzzle";
import GlobalInfoProvider from "../../../util/GlobalInfo";
import { TabStrip, TabStripSelectEventArguments, TabStripTab } from "@progress/kendo-react-layout";
import { EMAIL_CLIENT_PARAMS } from "../../../util/OpenMailLink/Types";
import OpenMailLink from "../../../util/OpenMailLink/OpenMailLink";

class SubmissionDetails extends Component<SUBMISSION_DETAILS_PROPS, SUBMISSION_DETAILS_STATE> {
  wrapRef: any;
  targetLangRef: any;
  targetLangLabelRef: any;
  ownerRef: any;
  ownerLabelRef: any;
  instructionsTabStripRef: any;

  state: SUBMISSION_DETAILS_STATE = {
    backgroundEnabled: false,
    details: {
      project: '',
      referenceFilesDownloadEnabled: false,
      dateStarted: '',
      dueDate: '',
      creator: '',
      languageDirections: [],
      name: '',
      sourceLanguage: '',
      submissionOwners: [],
      targetLanguages: [],
      submitterInfos: [],
      owners: []
    },
    project: {
      name: '',
      projectId: 0
    },
    documents: [],
    isLoading: true,
    instructions: '',
    refFilesEnabled: false,
    instructionsEnabled: false,
    instructionsSelectedTabStripTab: 0,
    notes: '',
    notesTabSelected: false,
  };

  setData(details: any) {
    const { isSupplierView, shouldNotesBeOpened } = this.props
    let documents = details.referenceFiles ? details.referenceFiles : [];
    let data = details
    if (isSupplierView) {
      data = {
        ...details,
        languageDirections: addDeletedTargetLanguages(_.isNull(details.languageDirections) ? [] : [details.languageDirections], [details.targetLanguage], []),
        dateStarted: details.startDate,
        owners: details.submissionOwners
      };
    } else {
      details.languageDirections = details.languageDirections.map((languageDirection: RESPONSE_LANGUAGE_DIRECTIONS) => {
        return {
          default: languageDirection.default,
          hasGlossary: languageDirection.hasGlossary,
          hasTmConfig: languageDirection.hasTmConfig,
          targetLanguage: languageDirection.targetLanguage,
          notInProject: !languageDirection.inProject,
          targetLanguageLocale: languageDirection.targetLanguageDisplayName
        }
      });
    }

    this.setState({
      details: data,
      documents,
      isLoading: false,
      instructions: details.instructions,
      instructionsEnabled: details.instructionsEnabled,
      instructionsSelectedTabStripTab: shouldNotesBeOpened ? 1 : 0,
      notes: details.background,
      backgroundEnabled: details.backgroundEnabled,
      notesTabSelected: shouldNotesBeOpened,
      project: { name: details.projectName },
      refFilesEnabled: details.refFilesEnabled
    });
  };


  /**
   * Set css class on browser resize
   * */
  setCssClassOnResize = () => {
    if (this.wrapRef?.offsetWidth < 900) {
      this.wrapRef.classList.add('wrap');
      this.targetLangLabelRef.style.height = `${this.targetLangRef.offsetHeight}px`;
      this.ownerLabelRef.style.height = `${this.ownerRef.offsetHeight}px`;
    } else {
      this.wrapRef.classList.remove('wrap');
      this.targetLangLabelRef.style.height = `${this.targetLangRef.offsetHeight}px`;
      this.ownerLabelRef.style.height = `${this.ownerRef.offsetHeight}px`;
    }
  }

  /**
   * @inheritDoc
   */
  moduleDidMount = () => {
    const { submissionId, projectId } = this.props.data;
    this.dispatch(this.props.actionName, Position.end, { submissionId, projectId });
    window.addEventListener('resize', this.setCssClassOnResize);
    this.setCssClassOnResize();
  };

  /**
   * Checks if props changed and gets details of new submission in that case
   * @param prevProps
   */
  componentDidUpdate(prevProps: SUBMISSION_DETAILS_PROPS): void {
    const data = this.props.data;
    if (!_.isEqual(prevProps.data, data)) {
      this.dispatch(this.props.actionName, Position.end, {
        submissionId: data.submissionId,
        projectId: data.projectId
      });
      this.setState({
        instructions: ''
      });
    }
    this.targetLangLabelRef.style.height = `${this.targetLangRef.offsetHeight}px`;
    this.ownerLabelRef.style.height = `${this.ownerRef.offsetHeight}px`;
  }

  /**
   * Reload's tab data
   */
  reloadDetails = () => {
    const { submissionId, projectId } = this.props.data;
    this.dispatch(this.props.actionName, Position.end, { submissionId, projectId });
  }

  /**
   * Remove event listener on cmp unmounting
   * */
  componentWillUnmount() {
    super.componentWillUnmount();
    window.removeEventListener('resize', this.setCssClassOnResize);
  }

  /**
   * Target languages render
   * @param targetLanguages
   */
  renderTargetLanguages = (targetLanguages: LANGUAGE_DIRECTIONS[]) => {
    return (
      <>
        {(targetLanguages).map((item: LANGUAGE_DIRECTIONS, index: number) => (
          <div
            className={`k-chip k-chip-md k-rounded-md k-chip-solid k-chip-solid-base ${createTargetLanguageTagClass(item)}`}
            key={`tp-target-lang-tag-${index}`}
          >
            {item.targetLanguageLocale}
          </div>
        ))}
      </>
    );
  };

  /**
   * Renders submission details panel
   */
  renderSubmissionDetailsTable = () => {
    let { details, project } = this.state;

    let {
      dateStarted = '',
      dueDate = '',
      sourceLanguage = '',
      languageDirections = [],
      submitterInfos = [],
    } = details;
    const { data, isSupplierView } = this.props;
    const { submissionId, submissionIdWordCount, submissionName } = data;
    let projectName = project ? project.name : '';

    let submitterInfosArray = submitterInfos ? submitterInfos.map((item) => {
      return item.firstName + ' ' + item.lastName;
    }) : [];
    let creator = submitterInfosArray.join(', ');

    let ownersData = isSupplierView ? details.submissionOwners : details.owners;
    let ownersArray = ownersData ? ownersData?.map((item) => {
      return item.firstName + ' ' + item.lastName;
    }) : ['-'];

    let ownersString = ownersArray.join(', ');
    let sourceLanguageLabel = isSupplierView ? this.translate('sourceLanguageTask') : this.translate('sourceLanguage');
    let targetLanguageLabel = isSupplierView ? this.translate('targetLanguageTask') : this.translate('targetLanguage', { numberOfLanguages: languageDirections.length });
    let sourceLanguageString = sourceLanguage ? getMatchingDisplayName([sourceLanguage], 'sourceLanguageValues', this.props.initializeUserData) : '';
    let targetLanguagesString: string | JSX.Element = '';
    if (languageDirections.length) {
      targetLanguagesString = isSupplierView ? getMatchingDisplayName(languageDirections, 'targetLanguageValues', this.props.initializeUserData, true) : this.renderTargetLanguages(languageDirections);
    }
    const columnHeaderClassName = GlobalInfoProvider.getThemeType() ? 'tp-color-accent' : 'tp-color-blue-300';

    let recipients: any[] = details.owners?.map((owners: any) => owners.emailAddress);
    let subject;
    if (isSupplierView) {
      subject = `${submissionIdWordCount} ${submissionName}`
    } else {
      subject = `${submissionId} ${submissionName}`
    }

    const emailClientParams: EMAIL_CLIENT_PARAMS = {
      recipients: recipients,
      subject: subject,
      message: ''
    }

    return (
      <Container
        ariaLabel={this.translate('SRL-detailsTable')}
        ariaLabeledBy={"drawer_details_section_title"}
        className="k-text-left tp-details-tab-table details"
        hBox
        role={"grid"}
        tabIndex={0}>
        <Container role={"rowgroup"} vBox>
          <Container role={"row"} vBox>
            <Container role={"columnheader"}
                       className={`tp-text-medium ${columnHeaderClassName}`}
                       hBox alignItemsEnd>
              <div className="k-text-ellipsis">{this.translate('project')}</div>
            </Container>
            <Container role={"columnheader"}
                       className={`tp-text-medium ${columnHeaderClassName}`}
                       hBox alignItemsEnd>
              <div className="k-text-ellipsis">{this.translate('dateStarted')}</div>
            </Container>
            <Container role={"columnheader"}
                       className={`tp-text-medium ${columnHeaderClassName}`}
                       hBox alignItemsEnd>
              <div className="k-text-ellipsis">{this.translate('dueDate')}</div>
            </Container>
            {
              this.props.isSupplierView ? null :
                <Container
                  role={"columnheader"}
                  className={`tp-text-medium ${columnHeaderClassName}`}
                  hBox alignItemsEnd>
                  <div className="k-text-ellipsis">{this.translate('creator')}</div>
                </Container>
            }
            <Container role={"columnheader"}
                       className={`tp-text-medium ${columnHeaderClassName}`}
                       hBox alignItemsEnd ref={div => this.ownerLabelRef = div}>
              <div className="k-text-ellipsis">{this.translate('owner')}</div>
            </Container>
            <Container role={"columnheader"}
                       className={`tp-text-medium ${columnHeaderClassName}`}
                       hBox alignItemsEnd>
              <div className="k-text-ellipsis">{this.translate(sourceLanguageLabel)}</div>
            </Container>
            <Container role={"columnheader"}
                       className={`tp-text-medium ${columnHeaderClassName}`}
                       hBox alignItemsStart ref={div => this.targetLangLabelRef = div}>
              <div className="k-text-ellipsis">{this.translate(targetLanguageLabel)}</div>
            </Container>
            <Container role={"columnheader"}
                       className={`tp-text-medium ${columnHeaderClassName}`}
                       hBox alignItemsEnd>
              <div className="k-text-ellipsis">{this.translate('wordCount')}</div>
            </Container>
          </Container>
        </Container>
        <Container role={"rowgroup"} vBox>
          <Container role={"row"} vBox>
            <Container role={"gridcell"} hBox alignItemsEnd>
              <div className="k-text-ellipsis">{projectName}</div>
            </Container>
            <Container role={"gridcell"} hBox alignItemsEnd>
              <div
                className="k-text-ellipsis">{dateStarted && this.translateByNamespace('date.dateWithTime', { date: new Date(dateStarted) })}</div>
            </Container>
            <Container role={"gridcell"} hBox alignItemsEnd>
              <div
                className="k-text-ellipsis">{dueDate && this.translateByNamespace('date.dateWithTime', { date: new Date(dueDate) })}</div>
            </Container>
            {isSupplierView ? null :
              <Container role={"gridcell"} hBox alignItemsEnd>
                <div className="k-text-ellipsis">{creator}</div>
              </Container>
            }
            <Container role={"gridcell"} className={'pd-project-manager-cell'} hBox alignItemsEnd
                       ref={div => this.ownerRef = div}>
              <OpenMailLink
                emailClientParams={emailClientParams}
                policy={'hasHyperlinkProjectManagerPolicy'}
              >
                {ownersString}
              </OpenMailLink>
            </Container>
            <Container role={"gridcell"} hBox alignItemsEnd>
              <div className="k-text-ellipsis">{sourceLanguageString}</div>
            </Container>
            <Container role={"gridcell"} className={'pd-target-cell-wrap'} gap={1} hBox alignItemsCenter wrap
                       ref={div => this.targetLangRef = div}>
              {targetLanguagesString}
            </Container>
            <Container role={"gridcell"} hBox alignItemsEnd>
              {isSupplierView ?
                <WordCountLink data={{
                  ...data,
                  isSupplierView: true,
                  status: { name: data.submissionStatus?.name },
                  taskId: data.submissionId,
                  submissionId: data.submissionIdWordCount
                }} onlyNumber={true} />
                :
                <WordCountLink data={{ ...data, status: { name: data.submissionStatus?.name } }} onlyNumber={true} />
              }
            </Container>
          </Container>
        </Container>
      </Container>
    )
  };

  /**
   * Renders a document in Documents panel
   */
  renderDocumentTable = () => {
    const { details, documents, refFilesEnabled } = this.state;
    const { isSupplierView } = this.props

    return (
      <Container
        ariaLabel={this.translate('SRL-referenceFilesTable')}
        ariaLabeledBy={'drawer_documents_section_title'}
        className="k-text-left tp-details-tab-table documents"
        vBox role="grid"
        tabIndex={0}
      >
        <Container role="rowgroup" vBox>
          <Container role="row" hBox>
            <Container role="columnheader" displayFlex alignItemsEnd className="tp-text-semibold">
              <div className="k-text-ellipsis">{this.translate('fileName')}</div>
            </Container>
            <Container role="columnheader" displayFlex alignItemsEnd className="tp-text-semibold">
              <div className="k-text-ellipsis">{this.translate('fileLevel')}</div>
            </Container>
          </Container>
        </Container>
        <Container role="rowgroup" vBox>
          {documents?.map((document: DOCUMENT, index: number) => {
            return (
              <React.Fragment key={document.fileName + index}>
                <Container role="row" hBox>
                  <Container role="gridcell" displayFlex alignItemsCenter>
                    <div className="k-text-ellipsis">{document.fileName}</div>
                  </Container>
                  <Container role="gridcell" displayFlex alignItemsCenter justifyContentBetween>
                    <div
                      className="k-text-ellipsis">{document.targetLanguageLocale ? document.targetLanguageValue : this.translate('submission')}</div>
                    {isSupplierView && refFilesEnabled &&
                      <PuzzleButton
                        disabled={isSupplierView ? !details.referenceFilesDownloadEnabled : false}
                        aria-label={this.translate('SRL-downloadALlDocuments')}
                        fillMode='flat'
                        iconClass="tp-icon tp-icon-lg tp-download"
                        className='tp-header-button'
                        onClick={() => this.downloadDocument(document)}
                      />}
                  </Container>
                </Container>
              </React.Fragment>
            );
          })}
        </Container>
      </Container>
    );
  };

  /**
   * DownloadAllDocuments button handler
   */
  downloadAllDocuments = () => {
    const downloadParams = { languageReferenceFiles: [], submissionReferenceFiles: false };
    const { documents = [] } = this.state;
    const { data, isSupplierView } = this.props
    const { submissionName, submissionId } = data
    let current;
    let item = documents.length;
    while (item) {
      item--;
      current = documents[item];
      if (current.targetLanguageLocale) {
        (downloadParams.languageReferenceFiles as string[]).push(current.targetLanguageLocale)
      } else if (!downloadParams.submissionReferenceFiles) {
        downloadParams.submissionReferenceFiles = true;
      }
    }
    const params: any = {};
    Object.assign(params,
      downloadParams.languageReferenceFiles.length ? { languageReferenceFiles: downloadParams.languageReferenceFiles } : null,
      downloadParams.submissionReferenceFiles ? { submissionReferenceFiles: downloadParams.submissionReferenceFiles } : null
    );

    if (isSupplierView) {
      const paramsReferenceFiles = { downloadType: 'references' };
      const submissionData = {
        taskId: submissionId,
        submissionName
      }
      this.dispatch('downloadFiles', '', { submissionData, params: paramsReferenceFiles });
    } else {
      this.dispatch('downloadReferenceFiles', '', {
        params: params
      });
    }

  };

  /**
   * Download instructions button handler
   */
  downloadInstructions = () => {
    let url: string = `/submissions/${this.props.data.submissionId}/downloadInstructions`;
    if (this.props.isSupplierView) {
      url = `/submissions/${this.props.data.submissionIdWordCount}/downloadInstructions?phaseType=${this.props.data.phaseType}`;
    }
    this.dispatch('downloadInstructions', '', { url });
  };

  /**
   * DownloadOneDocument button handler
   * @param ticket  Document ticket
   */
  downloadDocument = (ticket: any) => {
    const { repositoryTicket } = ticket
    const { data } = this.props;
    const submissionId = data.submissionIdWordCount;
    this.dispatch(
      'downloadDocument', '', {
        params: { repositoryTicket, submissionId, category: 'references' }
      }
    )

  };
  /**
   * Render for the empty sections when there are no instruction/documents
   * @param name  name of the section
   */
  emptySectionRender = (name?: string) => {
    let translateLabel = this.props.isSupplierView ? this.translate('emptyDocumentsSectionLabelTask') : this.translate('emptyDocumentsSectionLabel');

    if (name === 'instructions') {
      translateLabel = this.translate('emptyInstructionsSectionLabel');
    }

    if (name === 'notes') {
      translateLabel = this.translate('emptyNotesSectionLabel');
    }

    let imgClass: string = 'tp-grid-empty-state-img no-instructions'
    if (!_.includes(['instructions', 'notes'], name)) {
      imgClass = 'tp-grid-empty-state-img no-documents';
    }

    return (
      <Container vBox alignItemsCenter justifyContentCenter
                 className={`tp-empty-section-info ${name === 'documents' ? 'documents' : ''}`}>
        <div aria-hidden="true" className={imgClass} />
        <span className="tp-empty-section-info-text tp-text-semibold k-text-center">{translateLabel}</span>
      </Container>
    );
  }

  renderBody() {
    const { projectId } = this.props.data;
    const { isSupplierView } = this.props
    const {
      backgroundEnabled,
      details,
      documents = [],
      isLoading,
      instructions = '',
      instructionsEnabled,
      refFilesEnabled
    } = this.state;
    const { background = '' } = details;

    let showRefFileDownload = !!documents.length;
    let showDownload = !!instructions || !!background;
    let showInstructionsSection = true;
    if (isSupplierView) {
      showRefFileDownload = showRefFileDownload && refFilesEnabled;
      showInstructionsSection = instructionsEnabled;
    }

    let showRefFilesSection = refFilesEnabled;
    if (!this.props.isSupplierView) {
      showRefFilesSection = PoliciesManager.inProject(projectId)?.hasSubmissionReferenceFilesPolicy;
    }

    let referenceFilesContentRender: JSX.Element | null = null;
    if (!isLoading) {
      referenceFilesContentRender = documents.length ? this.renderDocumentTable() : this.emptySectionRender();
    }

    return (
      <Container
        className={'tp-drawer-body-tab-details-content'}
        ref={div => {
          this.wrapRef = div;
          this.props.setSubmissionDetailsTabRef?.(div);
        }}
      >
        <VBox gap={3}>
          <Container vBox className='tp-drawer-tab-details-section details'>
            <Container
              alignItemsCenter
              htmlTag={'h3'}
              hBox
              id={"drawer_details_section_title"}
            >
            <span className={'tp-text-semibold tp-font-size-md'}>
              {this.translate('submissionDetails')}
            </span>
            </Container>
            {this.renderSubmissionDetailsTable()}
          </Container>
          {showRefFilesSection &&
            <Container vBox className='tp-drawer-tab-details-section documents'>
              <Container
                htmlTag={'h3'}
                hBox
                alignItemsCenter
                justifyContentBetween
              >
              <span
                id={"drawer_documents_section_title"}
                className="tp-text-semibold tp-font-size-md"
              >
                {this.translate('documents')}
              </span>
                {showRefFileDownload && <PuzzleButton
                  disabled={isSupplierView ? !details.referenceFilesDownloadEnabled : false}
                  aria-label={this.translate('SRL-downloadALlDocuments')}
                  fillMode={isSupplierView ? 'solid' : 'flat'}
                  themeColor='primary'
                  iconClass={isSupplierView ? undefined : "tp-icon tp-icon-md tp-download"}
                  className='tp-header-button'
                  onClick={this.downloadAllDocuments}
                >{isSupplierView ? this.translate('SRL-downloadAll') : undefined}</PuzzleButton>}
              </Container>
              <Container flex1>
                {referenceFilesContentRender}
              </Container>
            </Container>
          }
        </VBox>
        {
          (showInstructionsSection || backgroundEnabled) &&
          <Container vBox className='tp-drawer-tab-details-section instructions'>
            <Container htmlTag={'h3'} hBox alignItemsCenter justifyContentBetween>
              <span className="tp-text-semibold tp-font-size-md">{this.translate('instructionsAndNotes')}</span>
              {showDownload && <PuzzleButton
                aria-label={this.translate('SRL-downloadInstructions')}
                fillMode='flat'
                themeColor={'primary'}
                iconClass="tp-icon tp-icon-md tp-download"
                className='tp-header-button'
                onClick={this.downloadInstructions}
              />}
            </Container>
            <Container flex1>
              {this.detailsTabStripRender()}
            </Container>
          </Container>
        }
      </Container>
    );
  }

  /**
   * Renders instructions, notes text or icon if text does not exist
   * @param notesTab
   */
  renderInstructionsOrNotes = (notesTab: boolean) => {
    const { instructions, notes, isLoading } = this.state;
    let textToRender: string = instructions || "<p></p>";
    let emptySectionRender: string = 'instructions';
    if (notesTab) {
      textToRender = notes || "<p></p>";
      emptySectionRender = 'notes';
    }

    let notesAndInstructionsRender: JSX.Element | null = null;
    if (!isLoading) {
      notesAndInstructionsRender = (textToRender !== "<p></p>") ?
        <div dangerouslySetInnerHTML={{ __html: textToRender }} /> : this.emptySectionRender(`${emptySectionRender}`)
    }

    return (
      notesAndInstructionsRender
    )
  }

  /**
   * Changes from instructions to notes and vice versa on click
   * @param e
   */
  onInstructionsTabStripChange = (e: TabStripSelectEventArguments) => {
    this.setState({ instructionsSelectedTabStripTab: e.selected });
  }

  /**
   * A TabStrip function component that changes which text is rendered on drawer (Notes or Instructions)
   */
  detailsTabStripRender = () => {
    const { isSupplierView } = this.props;
    let { backgroundEnabled, instructionsEnabled, instructionsSelectedTabStripTab: selectedTab } = this.state;
    let tabCount = 2;
    if (isSupplierView && !(backgroundEnabled && instructionsEnabled)) {
      tabCount = 1;
      selectedTab = 0;
    }

    return (
      <PuzzleTabStrip
        ref={(tabStrip: any) => this.instructionsTabStripRef = tabStrip}
        handleSelect={this.onInstructionsTabStripChange}
        tabCount={tabCount}
        translate={this.translate}
        selected={selectedTab}
      >
        <TabStrip
          selected={selectedTab}
        >
          {(!isSupplierView || instructionsEnabled) && <TabStripTab
            key={"instructions"}
            title={this.translate("instructions")}
            contentClassName="tp-no-border"
          >
            {this.renderInstructionsOrNotes(false)}
          </TabStripTab>}

          {(!isSupplierView || backgroundEnabled) && <TabStripTab
            key={"notes"}
            title={this.translate("notesTabStrip")}
            contentClassName="tp-no-border"
          >
            {this.renderInstructionsOrNotes(true)}
          </TabStripTab>}
        </TabStrip>
      </PuzzleTabStrip>
    );
  }
}

export default SubmissionDetails;
